import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

// Emergent-managed Google OAuth: after Google sign-in the browser lands back on
// this site with #session_id=... in the URL fragment. Detect it synchronously
// during render (useLocation().hash is reactive; window.location.hash is not)
// so the exchange runs before any route/auth check.

export const GoogleAuthHandler = ({ children }) => {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) return <GoogleAuthCallback />;
    return children;
};

const GoogleAuthCallback = () => {
    const { exchangeGoogleSession } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const processed = useRef(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (processed.current) return;
        processed.current = true;
        const sessionId = location.hash.match(/session_id=([^&]+)/)?.[1];
        if (!sessionId) {
            setError("Sign-in could not be completed. Please try again.");
            return;
        }
        exchangeGoogleSession(sessionId)
            .then(() => navigate("/", { replace: true }))
            .catch(() => setError("Google sign-in failed or the session expired. Please try again."));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-cream px-6" data-testid="google-auth-callback">
            {error ? (
                <>
                    <p data-testid="google-auth-error" className="max-w-sm rounded-2xl bg-red-50 px-5 py-4 text-center text-sm text-red-700">
                        {error}
                    </p>
                    <Link to="/login" data-testid="google-auth-back" className="rounded-full bg-ink px-7 py-3 text-sm font-medium text-cream transition-colors hover:bg-india">
                        Back to login
                    </Link>
                </>
            ) : (
                <>
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink/10 border-t-saffron" />
                    <p className="text-sm text-smoke">Signing you in with Google…</p>
                </>
            )}
        </div>
    );
};

export const GoogleButton = ({ label = "Continue with Google", testId = "google-login-button" }) => {
    const { loginWithGoogle } = useAuth();
    return (
        <button
            type="button"
            onClick={loginWithGoogle}
            data-testid={testId}
            className="flex w-full items-center justify-center gap-3 rounded-full border border-ink/15 bg-white px-8 py-4 text-base font-medium text-ink transition-colors hover:border-saffron hover:text-saffron"
        >
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
                <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
                <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
                <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
            </svg>
            {label}
        </button>
    );
};
